import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  faArrowDown,
  faArrowDownToLine,
  faArrowUp,
  faGripDotsVertical,
  faThumbsDown,
  faThumbsUp
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import { useFormContext } from 'react-hook-form'
import { TextareaInput } from '~/components/Form'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { lineKey, type ShortlistRankingValues } from '../forms/shortlistRanking'
import type { PublicShortlistApplicant } from '../types'
import {
  answerErrorMessage,
  answers,
  dropIndicator,
  dropIndicatorBottom,
  dropIndicatorTop,
  fileLink,
  files,
  grip,
  moveButton,
  moveControls,
  name,
  nameButton,
  nameLine,
  rank,
  row,
  rowBody,
  rowDragging,
  rowSelected,
  userComment,
  userCommentLabel
} from './PublicShortlist.css'
import { ShortlistAnswerButton } from './ShortlistAnswerButton'

export const SHORTLIST_LINE_DRAG_TYPE = 'shortlist-line'

type DropEdge = 'top' | 'bottom'

const getDropEdge = (element: Element, clientY: number): DropEdge => {
  const rect = element.getBoundingClientRect()
  return clientY < rect.top + rect.height / 2 ? 'top' : 'bottom'
}

type ShortlistRankingRowProps = {
  applicant: PublicShortlistApplicant
  position: number
  isSelected: boolean
  onSelect: () => void
  preview?: ReactNode
  onMoveUp?: () => void
  onMoveDown?: () => void
  onReject: (answers: ShortlistRankingValues['answers']) => void
}

export const ShortlistRankingRow = ({
  applicant,
  position,
  isSelected,
  onSelect,
  preview,
  onMoveUp,
  onMoveDown,
  onReject
}: ShortlistRankingRowProps): ReactElement => {
  const { register, control, getFieldState, formState } = useFormContext()
  const { getValues } = useFormContext<ShortlistRankingValues>()
  const rowRef = useRef<HTMLDivElement>(null)
  const gripRef = useRef<HTMLSpanElement>(null)
  const [dragging, setDragging] = useState(false)
  const [dropEdge, setDropEdge] = useState<DropEdge | null>(null)

  useEffect(() => {
    const element = rowRef.current
    const dragHandle = gripRef.current
    if (!element || !dragHandle) return

    const cleanupDrag = draggable({
      element,
      dragHandle,
      getInitialData: () => ({
        type: SHORTLIST_LINE_DRAG_TYPE,
        lineId: applicant.id
      }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false)
    })

    const cleanupDrop = dropTargetForElements({
      element,
      getData: ({ input }) => ({
        lineId: applicant.id,
        edge: getDropEdge(element, input.clientY)
      }),
      canDrop: ({ source }) =>
        source.data.type === SHORTLIST_LINE_DRAG_TYPE &&
        source.data.lineId !== applicant.id,
      onDrag: ({ self }) => {
        const edge = self.data.edge
        if (edge === 'top' || edge === 'bottom') setDropEdge(edge)
      },
      onDragLeave: () => setDropEdge(null),
      onDrop: () => setDropEdge(null)
    })

    return () => {
      cleanupDrag()
      cleanupDrop()
    }
  }, [applicant.id])

  const fieldName = `comments.${lineKey(applicant.id)}`
  const answerName = `answers.${lineKey(applicant.id)}`
  const answerError = getFieldState(answerName, formState).error

  return (
    <motion.div
      ref={rowRef}
      layout="position"
      className={clsx(row, isSelected && rowSelected, dragging && rowDragging)}
    >
      {dropEdge === 'top' && (
        <div className={clsx(dropIndicator, dropIndicatorTop)} aria-hidden />
      )}
      <div className={moveControls}>
        <span
          ref={gripRef}
          className={grip}
          title="Glisser pour déplacer ce profil"
          aria-hidden
        >
          <FontAwesomeIcon icon={faGripDotsVertical} />
        </span>
        <Button
          type="button"
          variation={ButtonVariation.Neutral}
          buttonSize="extraSmall"
          className={moveButton}
          title="Monter ce profil"
          aria-label="Monter ce profil"
          disabled={!onMoveUp}
          onClick={onMoveUp}
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </Button>
        <Button
          type="button"
          variation={ButtonVariation.Neutral}
          buttonSize="extraSmall"
          className={moveButton}
          title="Descendre ce profil"
          aria-label="Descendre ce profil"
          disabled={!onMoveDown}
          onClick={onMoveDown}
        >
          <FontAwesomeIcon icon={faArrowDown} />
        </Button>
      </div>
      <span className={rank}>{position}</span>
      <div className={rowBody}>
        <div className={nameLine}>
          <button
            type="button"
            className={clsx(name, nameButton)}
            onClick={onSelect}
            aria-pressed={isSelected}
          >
            {applicant.firstname} {applicant.lastname}
          </button>
          <div className={answers}>
            <ShortlistAnswerButton
              name={answerName}
              value="yes"
              icon={faThumbsUp}
              label="Retenir ce profil"
            />
            <ShortlistAnswerButton
              name={answerName}
              value="no"
              icon={faThumbsDown}
              label="Ne pas retenir ce profil"
              onAnswer={() => onReject(getValues('answers'))}
            />
          </div>
        </div>
        {answerError?.message && (
          <div className={answerErrorMessage}>{answerError.message}</div>
        )}
        {applicant.files.length > 0 && (
          <div className={files}>
            {applicant.files.map((file) => (
              <a key={file.url} className={fileLink} href={file.url}>
                <FontAwesomeIcon icon={faArrowDownToLine} />
                {file.filename}
              </a>
            ))}
          </div>
        )}
        {applicant.userComment && (
          <div className={userComment}>
            <span className={userCommentLabel}>
              Commentaire de votre interlocuteur
            </span>
            {applicant.userComment}
          </div>
        )}
        <TextareaInput
          name={fieldName}
          placeholder="Votre commentaire sur ce profil (facultatif)"
          aria-label={`Commentaire sur ${applicant.firstname} ${applicant.lastname}`}
          rows={2}
          register={register}
          control={control}
          error={getFieldState(fieldName, formState).error}
        />
        {preview}
      </div>
      {dropEdge === 'bottom' && (
        <div className={clsx(dropIndicator, dropIndicatorBottom)} aria-hidden />
      )}
    </motion.div>
  )
}
